import "./AddPicturesModal.scss"
import uploadPicture from "assets/img/upload.svg"
import uploadIcon from "assets/img/icon-upload.svg"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"

import React, { FunctionComponent, useState, useRef, useMemo, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep } from "lodash"
import PQueue from "p-queue"
import cx from "classnames"

import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"
import Modal from "components/Modal/Modal"
import Button from "components/Button/Button"
import { IMAGE_INPUT_FILE_ACCEPT, IMAGE_UPLOAD_MAX_SIZE_MB, IMAGE_SUPPORTED_TYPES_LABEL, IMAGE_SUPPORTED_EXTENSIONS, IMAGE_SUPPORTED_TYPES } from "config/constants"
import CustomError from "models/CustomError"
import DropFiles from "components/DropFiles/DropFiles"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import PictureSlider from "components/PictureSlider/PictureSlider"
import ThumbnailsSlider from "components/ThumbnailsSlider/ThumbnailsSlider"
import Input from "components/Input/Input"
import Tag from "components/Tag/Tag"
import fileUtils from "utils/file"
import DroppableArea from "components/DroppableArea/DroppableArea"

interface IAddPicturesModalProps {
    selectedFiles?: IFileToAdd[]
    fixedTags?: string[]
    tags?: string[]
    activeIndex?: number
    minPicturesCount?: number
    onSave(files: IFileToAdd[], tags: string[]): Promise<void> | void
    onClose(): void
}

export interface IFileToAdd {
    file: File
    preview: string
}

export const THUMBNAILS_MAX_COUNT = 8

const AddPicturesModal: FunctionComponent<React.PropsWithChildren<IAddPicturesModalProps>> = ({
    selectedFiles: initSelectedFiles,
    fixedTags = [],
    tags: initTags,
    activeIndex: initActiveIndex,
    minPicturesCount = 0,
    onSave,
    onClose
}) => {
    const { t } = useTranslation()
    const [isUploading, setIsUploading] = useState(false)
    const [activeIndex, setActiveIndex] = useState(initSelectedFiles?.length && typeof initActiveIndex === "number" && initActiveIndex >= 0 && initActiveIndex < initSelectedFiles.length ? initActiveIndex : 0)
    const [error, setError] = useState("")
    const [selectedFiles, setSelectedFiles] = useState<IFileToAdd[]>(initSelectedFiles ?? [])
    const [isLoadingPreviews, setIsLoadingPreviews] = useState(false)
    const inputFileEl = useRef<HTMLInputElement>(null)
    const [tagInputValue, setTagInputValue] = useState("")
    const [tags, setTags] = useState<string[]>(initTags ?? [])

    const canSwitchToPrev = activeIndex > 0
    const canSwitchToNext = activeIndex < selectedFiles.length - 1

    const currentFile = useMemo<IFileToAdd | null>(() => {
        return selectedFiles[activeIndex] || null
    }, [activeIndex, selectedFiles])

    const thumbnails = useMemo<string[]>(() => {
        return selectedFiles.map(f => f.preview)
    }, [selectedFiles])

    function handleInputFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files ? Array.from(event.target.files) : null
        event.target.value = ""
        if (!files || !files.length) return
        handleImagesSelect(files)
    }

    async function handleImagesSelect(files: File[]) {
        setError("")
        setIsLoadingPreviews(true)

        const validFiles: IFileToAdd[] = []
        const queue = new PQueue({ concurrency: 1 })

        const getPreview = async (file: File) => {
            try {
                const preview = await fileUtils.validateImageFile({
                    file,
                    extensions: IMAGE_SUPPORTED_EXTENSIONS,
                    types: IMAGE_SUPPORTED_TYPES,
                    maxSizeMb: IMAGE_UPLOAD_MAX_SIZE_MB,
                })
                validFiles.push({
                    file,
                    preview
                })
            } catch(e) {
                if (e instanceof CustomError) {
                    setError(e.message)
                } else {
                    setError(t("validation.failedToLoadImage"))
                }
            }
        }

        for (const file of files) {
            queue.add(getPreview.bind(null, file))
        }

        await queue.onIdle()

        setSelectedFiles([...selectedFiles, ...validFiles])
        setActiveIndex(0)
        setIsLoadingPreviews(false)
    }

    function handleDropFiles(files: File[]) {
        handleImagesSelect(files)
    }
    async function handleSave() {
        setIsUploading(true)
        try {
            await onSave(selectedFiles, tags)
        } catch(e) {
            setIsUploading(false)
            return
        }
        onClose()
    }

    const switchToPrev = useCallback(function() {
        if (!canSwitchToPrev) return
        setActiveIndex(Math.max(activeIndex - 1, 0))
    }, [activeIndex, canSwitchToPrev])

    const switchToNext = useCallback(async function() {
        if (!canSwitchToNext) return
        const newActiveIndex = activeIndex + 1
        setActiveIndex(newActiveIndex)
    }, [activeIndex, canSwitchToNext])

    function switchToIndex(index: number) {
        if (index < 0 || index > selectedFiles.length - 1 || index === activeIndex) return
        setActiveIndex(index)
    }

    const handleKeydown = useCallback((e: KeyboardEvent) => {
        if (e.key === "ArrowLeft") {
            switchToPrev()
        }
        if (e.key === "ArrowRight") {
            switchToNext()
        }
    }, [switchToNext, switchToPrev])

    useEffect(() => {
        document.addEventListener("keydown", handleKeydown)
        return () => document.removeEventListener("keydown", handleKeydown)
    }, [handleKeydown])

    function deleteActivePicture() {
        const newSelectedFiles = cloneDeep(selectedFiles)
        newSelectedFiles.splice(activeIndex, 1)
        setSelectedFiles(newSelectedFiles)
        if (activeIndex > newSelectedFiles.length - 1) {
            setActiveIndex(Math.max(newSelectedFiles.length - 1, 0))
        }
    }

    function handleTagInputKeyPress(e: React.KeyboardEvent) {
        if (e.key !== "Enter") return
        const trimmedValue = tagInputValue.trim()
        if (!trimmedValue) return
        setTags([...tags, trimmedValue])
        setTagInputValue("")
    }

    function removeTag(index: number) {
        const newTags = [...tags]
        newTags.splice(index, 1)
        setTags(newTags)
    }

    return (
        <Modal
            title={t('addPictures.title')}
            onClose={isUploading ? undefined : onClose}
            width="1000px"
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t('common.cancel')}
                        onClick={onClose}
                        disabled={isUploading} />
                    <Button
                        label={t('common.save')}
                        onClick={handleSave}
                        className="ml-2"
                        disabled={isUploading || selectedFiles.length < minPicturesCount}
                        loading={isUploading} />
                </Flex>
            }>

            {selectedFiles.length > 0 &&
                <div className="add-pictures-modal__slider mb-4">
                    <PictureSlider
                        imageUrl={currentFile?.preview}
                        hasPrev={canSwitchToPrev}
                        hasNext={canSwitchToNext}
                        onPrev={switchToPrev}
                        onNext={switchToNext}
                        additionalControls={
                            <>
                                {!isUploading &&
                                    <Button
                                        round
                                        theme="grey"
                                        icon={<IconTrashBin />}
                                        onClick={deleteActivePicture} />
                                }
                            </>
                        }
                    />
                </div>
            }

            <DropFiles onDrop={handleDropFiles} enabled={!isUploading}>
                {({ isDragOver }) => (
                    <DroppableArea isDragOver={isDragOver}>
                        <div className={cx(["add-pictures-modal__drop", {
                                "add-pictures-modal__drop--with-images": selectedFiles.length > 0
                            }])}>

                            {selectedFiles.length === 0 &&
                                <>
                                    <img src={uploadPicture} alt="" />
                                    <Text className="mb-4" preLine>{t('addPictures.choosePictureMaxX', { max: IMAGE_UPLOAD_MAX_SIZE_MB, formats: IMAGE_SUPPORTED_TYPES_LABEL })}</Text>
                                    <Button
                                        label={t('addPictures.choosePicture')}
                                        onClick={() => inputFileEl.current?.click()}
                                        loading={isLoadingPreviews}
                                        disabled={isLoadingPreviews} />
                                </>
                            }

                            {selectedFiles.length > 0 &&
                                <>
                                    <Flex className="mb-1" alignItems="center">
                                        <img src={uploadIcon} className="mr-1" alt="" />
                                        <Text>{t('addPictures.dropPicturesHere')} ({IMAGE_SUPPORTED_TYPES_LABEL})</Text>
                                    </Flex>
                                    <ThumbnailsSlider
                                        maxCountToShow={THUMBNAILS_MAX_COUNT}
                                        images={thumbnails}
                                        activeIndex={activeIndex}
                                        onSwitchToIndex={switchToIndex}
                                        onAdd={isUploading ? undefined : () => inputFileEl.current?.click()}
                                    />
                                </>
                            }

                            <input
                                type="file"
                                multiple
                                accept={IMAGE_INPUT_FILE_ACCEPT.join(",")}
                                className="add-pictures-modal__input-file"
                                onChange={handleInputFileChange}
                                ref={inputFileEl}
                                data-testid="add-pictures-input-file" />

                            {!!error &&
                                <div className={selectedFiles.length > 0 ? "mt-1" : "mt-3"}>
                                    <ValidationMessage>{error}</ValidationMessage>
                                </div>
                            }
                        </div>
                    </DroppableArea>
                )}
            </DropFiles>

            <div className="my-3">
                <Text className="text-center mb-2">{t('addPictures.addTag')}</Text>
                <Input
                    placeholder={t('addPictures.enterTag')}
                    value={tagInputValue}
                    onChange={e => setTagInputValue(e.target.value)}
                    onKeyPress={handleTagInputKeyPress}
                />
                <div className="mt-3">
                    {(fixedTags.length > 0 || tags.length > 0) &&
                        <Flex
                            justifyContent="center"
                            wrap="wrap">
                            {fixedTags.map((tag, idx) => (
                                <FlexItem key={idx} className="mb-1 mr-1 max-w-100">
                                    <Tag label={tag} />
                                </FlexItem>
                            ))}
                            {tags.map((tag, idx) => (
                                <FlexItem key={idx} className="mb-1 mr-1 max-w-100">
                                    <Tag label={tag} onClick={() => removeTag(idx)} crossIcon />
                                </FlexItem>
                            ))}
                        </Flex>
                    }
                    {fixedTags.length === 0 && tags.length === 0 &&
                        <Text className="color-grey-500 text-center">{t('addPictures.noTagsAdded')}</Text>
                    }
                </div>
            </div>
        </Modal>
    )
}

export default AddPicturesModal
